/*
 * Photo page layout presets.
 *
 * Two flavours:
 *  - `fullbleed` — photo fills the page, no frame.
 *  - everything else — decorative PNG frame; photo sits inside the slot
 *    (cover, no stretching). Frame + slot dimensions live alongside the asset.
 *
 * Persistence format for a photo's layout value:
 *   `<preset>::<datePos>:<dateSize>::<descPos>:<descSize>::<textColor>`
 * All parts after `<preset>` are optional; missing parts fall back to defaults.
 */

import squareFrame from '../../assets/frames/square.png';
import landscapeFrame from '../../assets/frames/landscape.png';
import landscapeWideFrame from '../../assets/frames/landscape-wide.png';
import portraitFrame from '../../assets/frames/portrait.png';
import portraitTallFrame from '../../assets/frames/portrait-tall.png';

export type PhotoPreset =
  | 'fullbleed'
  | 'square'
  | 'landscape-wide'
  | 'portrait-tall'
  | 'portrait'
  | 'landscape';

export type PhotoTextSlot = 'hidden' | 'top' | 'center' | 'bottom';
export type PhotoTextSize = 'sm' | 'md' | 'lg';
export type PhotoTextColor = 'light' | 'dark';

export interface PhotoFrameAsset {
  src: string;
  /** Frame intrinsic dimensions in px. */
  frameW: number;
  frameH: number;
  /** Photo slot dimensions in frame px (slot is centered inside frame). */
  slotW: number;
  slotH: number;
  /** Frame display width as a fraction of the page width. */
  widthRatio: number;
}

export interface PhotoPresetDefinition {
  id: PhotoPreset;
  /** null = full bleed, no decorative frame. */
  frame: PhotoFrameAsset | null;
}

export const PHOTO_PRESETS: PhotoPresetDefinition[] = [
  { id: 'fullbleed', frame: null },
  {
    id: 'square',
    frame: { src: squareFrame, frameW: 1103, frameH: 1086, slotW: 754, slotH: 759, widthRatio: 0.78 },
  },
  {
    id: 'landscape',
    frame: { src: landscapeFrame, frameW: 1176, frameH: 805, slotW: 903, slotH: 581, widthRatio: 0.80 },
  },
  {
    id: 'landscape-wide',
    frame: { src: landscapeWideFrame, frameW: 1174, frameH: 691, slotW: 975, slotH: 481, widthRatio: 0.82 },
  },
  {
    id: 'portrait',
    frame: { src: portraitFrame, frameW: 1193, frameH: 1690, slotW: 811, slotH: 1261, widthRatio: 0.65 },
  },
  {
    id: 'portrait-tall',
    frame: { src: portraitTallFrame, frameW: 805, frameH: 1347, slotW: 544, slotH: 1098, widthRatio: 0.56 },
  },
];

export const DEFAULT_PHOTO_PRESET: PhotoPreset = 'fullbleed';

export const photoPresetById = (id: string | null | undefined): PhotoPresetDefinition => {
  const match = PHOTO_PRESETS.find((p) => p.id === id);
  return match ?? PHOTO_PRESETS[0];
};

// ============== text slots ==============

export interface PhotoTextState {
  pos: PhotoTextSlot;
  size: PhotoTextSize;
}

export const DEFAULT_DATE_TEXT: PhotoTextState = { pos: 'top', size: 'sm' };
export const DEFAULT_DESC_TEXT: PhotoTextState = { pos: 'bottom', size: 'md' };
export const DEFAULT_TEXT_COLOR: PhotoTextColor = 'light';

// ============== encoding ==============

const encodeText = (t: PhotoTextState): string => `${t.pos}:${t.size}`;

const decodeText = (raw: string | undefined, fallback: PhotoTextState): PhotoTextState => {
  if (!raw) return fallback;
  const [posRaw, sizeRaw] = raw.split(':');
  const pos: PhotoTextSlot =
    posRaw === 'hidden' || posRaw === 'top' || posRaw === 'center' || posRaw === 'bottom'
      ? posRaw
      : fallback.pos;
  const size: PhotoTextSize =
    sizeRaw === 'sm' || sizeRaw === 'md' || sizeRaw === 'lg' ? sizeRaw : fallback.size;
  return { pos, size };
};

export interface PhotoLayout {
  preset: PhotoPreset;
  date: PhotoTextState;
  desc: PhotoTextState;
  color: PhotoTextColor;
}

export const DEFAULT_PHOTO_LAYOUT: PhotoLayout = {
  preset: DEFAULT_PHOTO_PRESET,
  date: DEFAULT_DATE_TEXT,
  desc: DEFAULT_DESC_TEXT,
  color: DEFAULT_TEXT_COLOR,
};

export const encodePhotoLayout = (layout: PhotoLayout): string =>
  [
    layout.preset,
    encodeText(layout.date),
    encodeText(layout.desc),
    layout.color,
  ].join('::');

export const decodePhotoLayout = (raw: string | null | undefined): PhotoLayout => {
  if (!raw) return DEFAULT_PHOTO_LAYOUT;
  const parts = raw.split('::');
  const presetRaw = parts[0];
  const preset: PhotoPreset = PHOTO_PRESETS.some((p) => p.id === presetRaw)
    ? (presetRaw as PhotoPreset)
    : DEFAULT_PHOTO_PRESET;
  const date = decodeText(parts[1], DEFAULT_DATE_TEXT);
  const desc = decodeText(parts[2], DEFAULT_DESC_TEXT);
  const colorRaw = parts[3];
  const color: PhotoTextColor =
    colorRaw === 'light' || colorRaw === 'dark' ? colorRaw : DEFAULT_TEXT_COLOR;
  return { preset, date, desc, color };
};
